/**
 * Tipo de gestão do prestador (aba "Outros Filtros") — espelha o enum
 * `ETipoGestaoPrestador` (sicom-api). Ausente/`null` = "Todas" (sem filtro).
 */
export enum ETipoGestaoPrestador {
    PUBLICA = 'PUBLICA',
    PRIVADA = 'PRIVADA',
    TERCEIRIZADA = 'TERCEIRIZADA'
}

/**
 * Filtros de pesquisa do Relatório por Seleção de Prestadores — espelha
 * `RelatorioSelecaoPjPesquisaRequest` (sicom-api), abas 1 a 9 do legado SIEM-CNP.
 */
export interface RelatorioSelecaoPjPesquisaFiltros {
    // Aba 1 — Prestador
    nuCrm: string;
    sgUf: string;
    nuCgcPj: string;
    regCad: string; // '' | 'C' (Cadastro) | 'R' (Registro)
    idSituacaoRegistro: string; // '' | 'S' (Ativo) | 'N' (Inativo)
    idTipoInscricao: number | null;
    idSituacao: number | null;
    dtSituacaoInicio: Date | null;
    dtSituacaoFim: Date | null;
    nmPrestador: string;
    nmFantasia: string;
    // Aba 2 — Especialidade
    idEspecialidade: string;
    idAreaAtuacao: string;
    // Aba 3 — R.H.
    idsRecursoHumano: number[];
    contemTodosRh: boolean;
    // Aba 4 — Recursos Materiais
    idsRecursoMaterial: number[];
    contemTodosRecursoMaterial: boolean;
    // Aba 5 — Serviços
    idsServico: number[];
    contemTodosServico: boolean;
    // Aba 6 — Atividades
    idsAtividade: number[];
    contemTodosAtividade: boolean;
    // Aba 1 — flag adicional: certificado (do ano mais recente) vencido.
    emitirApenasComCertificadoVencido: boolean;
    // Aba 7 — Histórico: situação histórica do prestador num período (ativa só com os 3 preenchidos).
    idSituacaoHistorico: number | null;
    dtHistoricoInicio: Date | null;
    dtHistoricoFim: Date | null;
    // Aba 8 — Matriz/Filial: código IN_TIPO_ESTABELECIMENTO ('' = Todos).
    tipoEstabelecimento: string;
    // Aba 9 — Outros Filtros
    idClassificacaoOpcao: number | null;
    tipoGestao: ETipoGestaoPrestador | null;
    dtInscricaoInicio: Date | null;
    dtInscricaoFim: Date | null;
    // Município OU Delegacia (abrangência) são mutuamente exclusivos.
    idMunicipio: number | null;
    idOrgaoDelegacia: number | null;
    sgUfDelegacia: string;
}

/** Referência de um prestador dentro de um lote (mesma chave composta do médico: CRM + UF). */
export interface PrestadorRefRequest {
    nuCrm: number;
    sgUf: string;
}

/**
 * Request para geração de um único PDF combinando N prestadores.
 * ⚠️ O endpoint `/exportar-pdf-lote` ainda não existe no backend (Task 8 do plano) — este tipo
 * já reflete o contrato esperado (mesmo padrão do médico) para não precisar retrabalho quando
 * a exportação for implementada, mas nenhum fluxo da UI o invoca hoje.
 */
export interface RelatorioSelecaoPjLoteExportRequest {
    prestadores: PrestadorRefRequest[];
    mostraCnpj: boolean;
    mostraNome: boolean;
    mostraSituacao: boolean;
    mostraClassificacao: boolean;
    mostraDiretoria: boolean;
    mostraCorpoSocietario: boolean;
    mostraServicos: boolean;
    mostraEnderecos: boolean;
    emissor: string;
}

/** Resposta do endpoint de geração síncrona (HTTP 200): PDF em base64. */
export interface RelatorioSelecaoPjExportResponse {
    pdf?: string | null;
    message?: string | null;
    file_name?: string | null;
}

/** Resposta do endpoint de geração assíncrona (HTTP 202): jobId para polling. */
export interface RelatorioSelecaoPjJobCreatedResponse {
    jobId: string;
}

/** Item da grade de resultados — espelha `RelatorioSelecaoPjListItemResponse` (sicom-api). */
export interface PrestadorListItem {
    idSolicitante: number;
    sgUf: string;
    nuCrm: number;
    nmPrestador: string;
    nuCgcPj: string;
    dsSituacao: string;
    dsClassificacao: string | null;
}

/** Item de catálogo genérico ({@code id + descrição}) usado nos filtros de R.H./Recursos/Serviços/Atividades. */
export interface CatalogoOpcaoResponse {
    id: number;
    descricao: string;
}
